import { create } from 'zustand';
import { API } from '../config/api';

// Constants
const POLLING_INTERVAL_MS = 5000;
const CLUB_POLLING_INTERVAL_MS = 3000;
const MAX_POLLING_ERRORS = 3;

// Safe JSON parsing helper
const safeJsonParse = async (res) => {
  try {
    return await res.json();
  } catch {
    return null;
  }
};

const useChatStore = create((set, get) => ({
  conversations: [],
  activeConversation: null,
  messages: [],
  loading: false,
  messagesLoading: false,
  error: null,
  searchResults: [],
  searchLoading: false,
  unreadCount: 0,
  pollingInterval: null,
  pollingErrorCount: 0,
  // Club chat state
  clubMessages: [],
  clubPollingInterval: null,
  clubPollingErrorCount: 0,
  // Message actions state
  editingMessage: null,
  forwardingMessage: null,

  // Fetch all conversations for a user
  fetchConversations: async (userId) => {
    if (!userId) return;
    set({ loading: true, error: null });
    try {
      const res = await fetch(`${API.CHAT}/conversations/${userId}`, { credentials: 'include' });
      const data = await safeJsonParse(res);
      if (res.ok && data) {
        set({ conversations: Array.isArray(data.conversations) ? data.conversations : [], loading: false });
      } else {
        set({ error: data?.error || 'Failed to fetch conversations', loading: false });
      }
    } catch (err) {
      set({ error: err.message, loading: false });
    }
  },

  // Fetch messages for a conversation
  fetchMessages: async (conversationId, userId, silent = false) => {
    if (!conversationId || !userId) return { success: false };
    if (!silent) {
      set({ messagesLoading: true, error: null });
    }
    try {
      const res = await fetch(`${API.CHAT}/messages/${conversationId}?userId=${userId}`, { credentials: 'include' });
      const data = await safeJsonParse(res);
      if (res.ok && data) {
        const newMessages = Array.isArray(data.messages) ? data.messages : [];
        const currentMessages = get().messages;

        // Only update if messages actually changed (compare by last message id and count)
        const hasChanges =
          newMessages.length !== currentMessages.length ||
          (newMessages.length > 0 && currentMessages.length > 0 &&
           newMessages[newMessages.length - 1]?.id !== currentMessages[currentMessages.length - 1]?.id);

        if (hasChanges || !silent) {
          set({ messages: newMessages, messagesLoading: false, pollingErrorCount: 0 });
          // Mark messages as read only if there are new messages
          if (hasChanges) {
            get().markMessagesAsRead(conversationId, userId);
          }
        } else {
          // Reset error count on success even without changes
          set({ pollingErrorCount: 0 });
        }
        return { success: true };
      } else {
        if (!silent) {
          set({ error: data?.error || 'Failed to fetch messages', messagesLoading: false });
        }
        return { success: false };
      }
    } catch (err) {
      if (!silent) {
        set({ error: err.message, messagesLoading: false });
      }
      return { success: false };
    }
  },

  // Send a message
  sendMessage: async (senderId, receiverId, text, audioUrl = null, audioDuration = null) => {
    if (!senderId || !receiverId) return { success: false, error: 'Missing sender or receiver' };
    try {
      const res = await fetch(`${API.CHAT}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ senderId, receiverId, text, audioUrl, audioDuration }),
      });
      const data = await safeJsonParse(res);
      if (res.ok && data?.message) {
        // Add message to local state
        set(state => ({
          messages: [...state.messages, data.message],
        }));
        // Update conversation's last message in list
        get().fetchConversations(senderId);
        return { success: true, message: data.message, conversationId: data.conversationId };
      } else {
        return { success: false, error: data?.error || 'Failed to send message' };
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[ChatStore] Send error:', err);
      }
      return { success: false, error: err.message };
    }
  },

  // Mark messages as read
  markMessagesAsRead: async (conversationId, userId) => {
    if (!conversationId || !userId) return;
    try {
      await fetch(`${API.CHAT}/messages/read`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ conversationId, userId }),
      });
      // Update unread count
      get().fetchUnreadCount(userId);
      // Update conversations to reflect read status
      set(state => ({
        conversations: state.conversations.map(c =>
          c.id === conversationId ? { ...c, unreadCount: 0 } : c
        ),
      }));
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error marking messages as read:', err);
      }
    }
  },

  // Get unread count
  fetchUnreadCount: async (userId) => {
    if (!userId) return;
    try {
      const res = await fetch(`${API.CHAT}/unread-count/${userId}`, { credentials: 'include' });
      const data = await safeJsonParse(res);
      if (res.ok && data) {
        const count = data.count != null ? parseInt(data.count, 10) : 0;
        set({ unreadCount: Number.isNaN(count) ? 0 : count });
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching unread count:', err);
      }
    }
  },

  // Search users for new chat
  // userRole is used to filter results - students can only see teachers
  searchUsers: async (query, excludeUserId, userRole = null) => {
    if (!query || query.length < 1) {
      set({ searchResults: [] });
      return;
    }
    set({ searchLoading: true });
    try {
      let url = `${API.CHAT}/search-users?query=${encodeURIComponent(query)}&excludeUserId=${excludeUserId}`;
      if (userRole) {
        url += `&userRole=${userRole}`;
      }
      const res = await fetch(url, { credentials: 'include' });
      const data = await safeJsonParse(res);
      if (res.ok && data) {
        set({ searchResults: Array.isArray(data.users) ? data.users : [], searchLoading: false });
      } else {
        set({ searchResults: [], searchLoading: false });
      }
    } catch (err) {
      set({ searchResults: [], searchLoading: false });
    }
  },

  // Start or get existing conversation with a user
  startConversation: async (userId, otherUserId) => {
    if (!userId || !otherUserId) return { exists: false, conversationId: null };
    try {
      const res = await fetch(`${API.CHAT}/conversation-with/${userId}/${otherUserId}`, { credentials: 'include' });
      const data = await safeJsonParse(res);
      return data || { exists: false, conversationId: null };
    } catch (err) {
      return { exists: false, conversationId: null };
    }
  },

  // Set active conversation
  setActiveConversation: (conversation, preserveMessages = false) => {
    const currentConversation = get().activeConversation;

    // Skip if same conversation already selected
    if (currentConversation?.id === conversation?.id) {
      return;
    }

    if (preserveMessages) {
      set({ activeConversation: conversation });
    } else {
      set({ activeConversation: conversation, messages: [] });
    }
  },

  // Clear search results
  clearSearchResults: () => {
    set({ searchResults: [] });
  },

  // Start polling for new messages
  startPolling: (conversationId, userId) => {
    if (!conversationId || !userId) return;
    // Clear existing interval
    const existingInterval = get().pollingInterval;
    if (existingInterval) {
      clearInterval(existingInterval);
    }

    // Reset error count
    set({ pollingErrorCount: 0 });

    // Poll for new messages (silent mode - no loading indicator)
    const interval = setInterval(async () => {
      if (conversationId && userId) {
        const result = await get().fetchMessages(conversationId, userId, true);
        if (!result?.success) {
          const errorCount = get().pollingErrorCount + 1;
          set({ pollingErrorCount: errorCount });
          // Stop polling after max consecutive errors
          if (errorCount >= MAX_POLLING_ERRORS) {
            if (process.env.NODE_ENV === 'development') {
              console.warn('[ChatStore] Stopping polling after consecutive errors');
            }
            get().stopPolling();
          }
        }
      }
    }, POLLING_INTERVAL_MS);

    set({ pollingInterval: interval });
  },

  // Stop polling
  stopPolling: () => {
    const interval = get().pollingInterval;
    if (interval) {
      clearInterval(interval);
      set({ pollingInterval: null });
    }
  },

  // Clear all state
  clearChat: () => {
    get().stopPolling();
    get().stopClubPolling();
    set({
      conversations: [],
      activeConversation: null,
      messages: [],
      searchResults: [],
      unreadCount: 0,
      clubMessages: [],
      editingMessage: null,
      forwardingMessage: null,
    });
  },

  // ========== MESSAGE ACTIONS FUNCTIONS ==========

  // Delete message (soft delete)
  deleteMessage: async (messageId, userId) => {
    try {
      const res = await fetch(`${API.CHAT}/messages/${messageId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId }),
      });

      if (res.ok) {
        // Remove from local state
        set(state => ({
          messages: state.messages.filter(m => m.id !== messageId),
        }));
        return { success: true };
      }
      const data = await safeJsonParse(res);
      return { success: false, error: data?.error || 'Failed to delete message' };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  // Edit message
  editMessage: async (messageId, userId, newText) => {
    try {
      const res = await fetch(`${API.CHAT}/messages/${messageId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId, newText }),
      });

      const data = await safeJsonParse(res);
      if (res.ok && data?.success) {
        // Update in local state
        set(state => ({
          messages: state.messages.map(m =>
            m.id === messageId
              ? { ...m, text: newText, editedAt: data.message.editedAt }
              : m
          ),
          editingMessage: null,
        }));
        return { success: true };
      }
      return { success: false, error: data?.error || 'Failed to edit message' };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  // Forward message to multiple conversations
  forwardMessage: async (messageId, senderId, conversationIds) => {
    try {
      const res = await fetch(`${API.CHAT}/messages/forward`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ messageId, senderId, conversationIds }),
      });

      const data = await safeJsonParse(res);
      if (res.ok && data?.success) {
        set({ forwardingMessage: null });
        // Refresh conversations to update last message
        get().fetchConversations(senderId);
        return { success: true, forwardedCount: data.forwardedCount };
      }
      return { success: false, error: data?.error || 'Failed to forward message' };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  // Toggle like on message
  toggleLike: async (messageId, userId) => {
    try {
      const res = await fetch(`${API.CHAT}/messages/${messageId}/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId }),
      });

      const data = await safeJsonParse(res);
      if (res.ok && data?.success) {
        // Update in local state
        set(state => ({
          messages: state.messages.map(m =>
            m.id === messageId
              ? { ...m, likedByMe: data.liked, likeCount: data.likeCount }
              : m
          ),
        }));
        return { success: true, liked: data.liked };
      }
      return { success: false, error: data?.error || 'Failed to toggle like' };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  // Set message for editing
  setEditingMessage: (message) => set({ editingMessage: message }),

  // Set message for forwarding
  setForwardingMessage: (message) => set({ forwardingMessage: message }),

  // Clear action states
  clearActionStates: () => set({
    editingMessage: null,
    forwardingMessage: null,
  }),

  // ========== CLUB GROUP CHAT FUNCTIONS ==========

  // Fetch messages for a club chat session
  fetchClubMessages: async (sessionId, silent = false) => {
    if (!sessionId) return { success: false };
    if (!silent) {
      set({ messagesLoading: true, error: null });
    }
    try {
      const res = await fetch(`${API.CHAT}/club/${sessionId}/messages`, { credentials: 'include' });
      const data = await safeJsonParse(res);
      if (res.ok && data) {
        const newMessages = Array.isArray(data.messages) ? data.messages : [];
        const currentMessages = get().clubMessages;

        // Only update if messages actually changed
        const hasChanges =
          newMessages.length !== currentMessages.length ||
          (newMessages.length > 0 && currentMessages.length > 0 &&
           newMessages[newMessages.length - 1]?.id !== currentMessages[currentMessages.length - 1]?.id);

        if (hasChanges || !silent) {
          set({ clubMessages: newMessages, messagesLoading: false, clubPollingErrorCount: 0 });
        } else {
          set({ clubPollingErrorCount: 0 });
        }
        return { success: true };
      } else {
        if (!silent) {
          set({ error: data?.error || 'Failed to fetch club messages', messagesLoading: false });
        }
        return { success: false };
      }
    } catch (err) {
      if (!silent) {
        set({ error: err.message, messagesLoading: false });
      }
      return { success: false };
    }
  },

  // Send a message to club chat
  sendClubMessage: async (sessionId, clubId, senderId, senderName, senderAvatarUrl, text, audioUrl = null, audioDuration = null) => {
    if (!sessionId || !senderId) return { success: false, error: 'Missing session or sender' };
    try {
      const res = await fetch(`${API.CHAT}/club/${sessionId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clubId, senderId, senderName, senderAvatarUrl, text, audioUrl, audioDuration }),
      });
      const data = await safeJsonParse(res);
      if (res.ok && data?.message) {
        // Add message to local state
        set(state => ({
          clubMessages: [...state.clubMessages, data.message],
        }));
        return { success: true, message: data.message };
      } else {
        return { success: false, error: data?.error || 'Failed to send message' };
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[ChatStore] Club send error:', err);
      }
      return { success: false, error: err.message };
    }
  },

  // Start polling for club messages
  startClubPolling: (sessionId) => {
    if (!sessionId) return;
    // Clear existing interval
    const existingInterval = get().clubPollingInterval;
    if (existingInterval) {
      clearInterval(existingInterval);
    }

    // Reset error count
    set({ clubPollingErrorCount: 0 });

    // Poll for club chat messages (more frequent for group chat)
    const interval = setInterval(async () => {
      if (sessionId) {
        const result = await get().fetchClubMessages(sessionId, true);
        if (!result?.success) {
          const errorCount = get().clubPollingErrorCount + 1;
          set({ clubPollingErrorCount: errorCount });
          if (errorCount >= MAX_POLLING_ERRORS) {
            if (process.env.NODE_ENV === 'development') {
              console.warn('[ChatStore] Stopping club polling after consecutive errors');
            }
            get().stopClubPolling();
          }
        }
      }
    }, CLUB_POLLING_INTERVAL_MS);

    set({ clubPollingInterval: interval });
  },

  // Stop polling for club messages
  stopClubPolling: () => {
    const interval = get().clubPollingInterval;
    if (interval) {
      clearInterval(interval);
      set({ clubPollingInterval: null, clubMessages: [] });
    }
  },
}));

export default useChatStore;
